/**
 * @lodex-pro/web-admin/features/disposal/hooks — the Disposal page (Phase 21 / Task 21.7 follow-up).
 *
 * TanStack Query hooks over the existing PLATFORM_ADMIN disposal API (all gated
 * MANAGE_DISPOSAL_PORTAL_ACCESS):
 *   GET   /v1/platform/disposal-companies                    list (portalStatus, limit, offset)
 *   POST  /v1/platform/disposal-companies/:id/activate       PAYMENT_PENDING → ACTIVE
 *   PATCH /v1/platform/disposal-companies/:id/modules        partial set of the 10 modules
 *   POST  /v1/platform/disposal-companies/:id/suspend        ACTIVE → SUSPENDED, reason (0234)
 *   POST  /v1/platform/disposal-companies/:id/reactivate     SUSPENDED → ACTIVE, reason (0234)
 *   GET   /v1/platform/disposal-registrations                queue (status, cursor)
 *   POST  /v1/platform/disposal-registrations/:id/approve    link to a company XOR create one
 *   POST  /v1/platform/disposal-registrations/:id/reject     reason, notify
 *
 * `type` imports only from the shared contracts (DEC-149 §Decision 9): the zod schemas stay on
 * the API side.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import type {
  DisposalModuleKey,
  DisposalPortalStatus,
} from '@lodex-pro/shared/validation/platform-disposal-companies';
import type {
  DisposalLicenseMatchStatus,
  DisposalRegistrationStatus,
} from '@lodex-pro/shared/validation/platform-disposal-registrations';

export type {
  DisposalLicenseMatchStatus,
  DisposalModuleKey,
  DisposalPortalStatus,
  DisposalRegistrationStatus,
};

export type DisposalModules = Readonly<Record<DisposalModuleKey, boolean>>;

export interface DisposalCompanyRow {
  readonly id: string;
  readonly tenantId: string;
  readonly tenantSlug: string;
  readonly tenantName: string;
  readonly name: string;
  readonly legalName: string | null;
  readonly primaryContactEmail: string | null;
  readonly portalStatus: DisposalPortalStatus;
  readonly activatedAt: string | null;
  readonly activatedByUserId: string | null;
  readonly modules: DisposalModules;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface DisposalCompanyList {
  readonly items: readonly DisposalCompanyRow[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

export interface DisposalRegistrationRow {
  readonly id: string;
  readonly email: string;
  readonly companyName: string;
  readonly contactName: string;
  readonly phone: string;
  readonly licenseNumber: string | null;
  readonly state: string | null;
  readonly facilityName: string | null;
  readonly matchedFacilityId: string | null;
  readonly matchedFacilityDisplayName: string | null;
  readonly licenseMatchStatus: DisposalLicenseMatchStatus;
  readonly status: DisposalRegistrationStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly emailVerifiedAt: string | null;
  readonly linkedTenantId: string | null;
}

export interface DisposalRegistrationList {
  readonly registrations: readonly DisposalRegistrationRow[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

export type ApproveDisposalBody =
  | {
      readonly disposalCompanyId: string;
      readonly manualVerificationNote?: string;
    }
  | {
      readonly createNewDisposalCompany: {
        readonly name: string;
        readonly slug?: string;
        readonly legalName?: string;
      };
      readonly manualVerificationNote?: string;
    };

export interface ApproveDisposalResponse {
  readonly registrationId: string;
  readonly tenantId: string;
  readonly userId: string;
  readonly disposalCompanyId: string;
  readonly portalStatus: DisposalPortalStatus;
}

export interface RejectDisposalBody {
  readonly reason: string;
  readonly notifyApplicant: boolean;
}

/** The page size for the companies table (the list endpoint allows up to 100). */
export const COMPANY_PAGE_SIZE = 50;

// ─── Query keys ──────────────────────────────────────────────────────────────

export const DISPOSAL_COMPANIES_KEY = ['platform', 'disposal-companies'] as const;
export const DISPOSAL_REGISTRATIONS_KEY = ['platform', 'disposal-registrations'] as const;

// ─── Companies ───────────────────────────────────────────────────────────────

export function useDisposalCompanies(
  portalStatus: DisposalPortalStatus | null,
  offset: number,
  limit: number = COMPANY_PAGE_SIZE,
): ReturnType<typeof useApiQuery<DisposalCompanyList>> {
  const opts = useApiOpts();
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (portalStatus !== null) params.set('portalStatus', portalStatus);
  return useApiQuery<DisposalCompanyList>(
    opts,
    `/v1/platform/disposal-companies?${params.toString()}`,
    {
      queryKey: [...DISPOSAL_COMPANIES_KEY, portalStatus ?? 'ALL', offset, limit],
      staleTime: 10_000,
    },
  );
}

export function useActivateDisposalCompany(
  id: string,
  onDone: () => void,
): ReturnType<
  typeof useApiMutation<Record<string, never>, { portalStatus: DisposalPortalStatus }>
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<Record<string, never>, { portalStatus: DisposalPortalStatus }>(
    opts,
    `/v1/platform/disposal-companies/${id}/activate`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: DISPOSAL_COMPANIES_KEY });
        onDone();
      },
    },
  );
}

export function useSetDisposalModules(
  id: string,
  onDone: () => void,
): ReturnType<
  typeof useApiMutation<Partial<Record<DisposalModuleKey, boolean>>, { state: DisposalModules }>
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<Partial<Record<DisposalModuleKey, boolean>>, { state: DisposalModules }>(
    opts,
    `/v1/platform/disposal-companies/${id}/modules`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: DISPOSAL_COMPANIES_KEY });
        onDone();
      },
    },
  );
}

/** POST /:id/suspend (ACTIVE → SUSPENDED) or /:id/reactivate (SUSPENDED → ACTIVE), migration 0234. */
export type DisposalStatusAction = 'suspend' | 'reactivate';

export function useSetDisposalCompanyStatus(
  id: string,
  action: DisposalStatusAction,
  onDone: () => void,
): ReturnType<typeof useApiMutation<{ reason: string }, { portalStatus: DisposalPortalStatus }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ reason: string }, { portalStatus: DisposalPortalStatus }>(
    opts,
    `/v1/platform/disposal-companies/${id}/${action}`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: DISPOSAL_COMPANIES_KEY });
        onDone();
      },
    },
  );
}

// ─── Registrations ───────────────────────────────────────────────────────────

export function useDisposalRegistrations(
  status: DisposalRegistrationStatus,
  cursor: string | null,
): ReturnType<typeof useApiQuery<DisposalRegistrationList>> {
  const opts = useApiOpts();
  const params = new URLSearchParams({ status });
  if (cursor !== null) params.set('cursor', cursor);
  return useApiQuery<DisposalRegistrationList>(
    opts,
    `/v1/platform/disposal-registrations?${params.toString()}`,
    {
      queryKey: [...DISPOSAL_REGISTRATIONS_KEY, status, cursor ?? 'first'],
      staleTime: 10_000,
    },
  );
}

export function useApproveDisposalRegistration(
  id: string,
  onDone: () => void,
): ReturnType<typeof useApiMutation<ApproveDisposalBody, ApproveDisposalResponse>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<ApproveDisposalBody, ApproveDisposalResponse>(
    opts,
    `/v1/platform/disposal-registrations/${id}/approve`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: DISPOSAL_REGISTRATIONS_KEY });
        // A new company, or a user linked to one, shows on the Companies tab.
        void qc.invalidateQueries({ queryKey: DISPOSAL_COMPANIES_KEY });
        onDone();
      },
    },
  );
}

export function useRejectDisposalRegistration(
  id: string,
  onDone: () => void,
): ReturnType<typeof useApiMutation<RejectDisposalBody, { status: 'REJECTED' }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<RejectDisposalBody, { status: 'REJECTED' }>(
    opts,
    `/v1/platform/disposal-registrations/${id}/reject`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: DISPOSAL_REGISTRATIONS_KEY });
        onDone();
      },
    },
  );
}
